import { useAtom } from 'jotai';
import React, { useEffect, useRef } from 'react';
import { useBundleProps } from 'signer-app/bundle-props';
import {
  useSignerAppParams,
  useSignerContext,
} from 'signer-app/signer-experience/signer-experience';
import { DrawerToolbar } from 'signer-app/signer-experience/signer-toolbar/drawer-toolbar';
import { PreviewStepToolbar } from 'signer-app/signer-experience/signer-toolbar/preview-step-toolbar';
import { ReviewStepToolbar } from 'signer-app/signer-experience/signer-toolbar/review-step-toolbar';
import { SignStepToolbar } from 'signer-app/signer-experience/signer-toolbar/sign-step-toolbar';
import {
  AccessibleDocumentToggle,
  accessibleViewAtom,
} from 'signer-app/signer-signature-document/accessible-document-toggle';
import { useAttachmentsInfo } from 'signer-app/utils/use-attachments-info';
import { useShouldShowDrawer } from 'signer-app/utils/use-should-show-drawer';

interface Props {
  showNextButton: boolean;
  toolbarMenu: React.ReactNode;
}

export const ToolbarContent = ({ showNextButton, toolbarMenu }: Props) => {
  const shouldUseDrawer = useShouldShowDrawer();
  const { customLogoUri, hasCustomStyles } = useBundleProps();
  const { step } = useSignerAppParams();

  const {
    isMobileReady,
    requiredFieldsLeft,
    esignDisclosure,
    defaultToFormView,
    hideFormViewToggle,
  } = useSignerContext();

  const { requiredAttachmentsLeft } = useAttachmentsInfo();

  // Stash the initial value of the user opt-in for esign disclosure. If the
  // user has already opted in, we don't want to show the disclosure
  // section. If they haven't, we want to show the checkbox and keep it
  // visible for the duration of the session.
  const esignOptedInInitially = useRef(esignDisclosure?.userOptIn ?? false);

  const mobileToggle =
    isMobileReady && !hideFormViewToggle ? (
      <AccessibleDocumentToggle
        disable={shouldUseDrawer && step === 'review'}
      />
    ) : null;

  const colorMode = 'bright';
  const [isAccessibleViewEnabled, setAccessibleView] =
    useAtom(accessibleViewAtom);
  useEffect(() => {
    if (defaultToFormView) {
      setAccessibleView(true);
    }
  }, [defaultToFormView, setAccessibleView]);

  const drawer = (
    <DrawerToolbar toolbarMenu={toolbarMenu} mobileToggle={mobileToggle} />
  );

  if (step === 'preview') {
    return (
      <PreviewStepToolbar
        showNextButton={showNextButton}
        colorMode={colorMode}
        customLogoUri={customLogoUri}
        toolbarMenu={toolbarMenu}
        mobileToggle={mobileToggle}
        requiredFieldsLeft={requiredFieldsLeft}
        requiredAttachmentsLeft={requiredAttachmentsLeft}
        hasCustomStyles={hasCustomStyles}
        showEsignDisclosure={Boolean(
          esignDisclosure?.enabled && !esignOptedInInitially.current,
        )}
      />
    );
  }

  if (step === 'sign' || step === 'attachments') {
    if (shouldUseDrawer && !isAccessibleViewEnabled) {
      return drawer;
    }

    return (
      <SignStepToolbar
        showNextButton={showNextButton}
        toolbarMenu={toolbarMenu}
        mobileToggle={mobileToggle}
        requiredFieldsLeft={requiredFieldsLeft}
      />
    );
  }

  if (step === 'review') {
    return <ReviewStepToolbar showNextButton={showNextButton} />;
  }

  return null;
};
